import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAtom } from 'jotai';
import { documentFilterState } from '@/recoil/atom';
import useModal from '@/hooks/useModal';
import IntranetLibraryLabel from '@/components/@IntranetLibrary/Label';
import RHFTextArea from '@/components/HookForm/RHFTextarea';
import { FormGroup } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import {
  StyledCancelButton,
  StyledSubmitButton,
  SubmitContainer,
} from '@/components/Approval/RightContainer/Favorites/styles';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import { t } from 'i18next';
import { useSnackbar } from '@/hooks/global-tools';
import {
  useSetTalkFlowFavoriteFilter,
  useGetMyTalkFlowFavoriteFilter,
  useModifyTalkFlowFavoriteFilter,
} from '@/hooks/apiHooks';
import Spinner from '@/components/@IntranetLibrary/Spinner';

type FormType = {
  subject: string;
};

type ModifyItem = {
  title: string;
  id: string;
};

type P = {
  handleClose?: () => void;
  modifyItem?: ModifyItem;
  defaultValue?: string;
};

export const useFavoritesModal = ({
  handleClose,
  modifyItem,
  defaultValue,
}: P = {}) => {
  const isModify = !!modifyItem;
  const favoriteModalRef = useRef<ModalRef>(null);
  const toast = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<FormType>({
    defaultValues: { subject: defaultValue ?? '' },
    mode: 'onChange',
  });

  const { mutate } = useGetMyTalkFlowFavoriteFilter();

  useEffect(() => {
    modifyItem && methods.setValue('subject', modifyItem.title);
  }, [modifyItem]);

  useEffect(() => {
    defaultValue && methods.setValue('subject', defaultValue);
  }, [defaultValue]);

  const { openModal, closeModal } = useModal(
    favoriteModalRef,
    undefined,
    () => {
      handleClose?.();
    },
  );

  const [filters] = useAtom(documentFilterState);

  const {
    formState: { isValid },
  } = methods;

  const onSubmit = async (obj: FormType) => {
    const org = {
      name: obj.subject,
      description: '',
      params: {
        createdBy: filters.createdBy,
        internalParticipants: filters.internalParticipants,
        requireAllInternalParticipants: filters.requireAllInternalParticipants,
        documentTypes: filters.documentTypes,
        keyword: filters.keyword,
        searchFields: filters.searchFields,
        filterName: obj.subject,
        createdStartedAt: filters.createdStartedAt,
        createdEndedAt: filters.createdEndedAt,
        meetingStartedAt: filters.meetingStartedAt,
        meetingEndedAt: filters.meetingEndedAt,
      },
    };

    setIsLoading(true);
    try {
      if (modifyItem) {
        await useModifyTalkFlowFavoriteFilter({
          id: modifyItem.id,
          name: obj.subject,
        });
      } else {
        await useSetTalkFlowFavoriteFilter(org);
      }
      await mutate();
      toast?.show({
        message: t(
          modifyItem
            ? '내 필터 이름이 수정되었습니다.'
            : '내 필터로 저장되었습니다.',
        ),
        severity: 'success',
      });
      closeModal();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const FavoriteModal = (
    <div onClick={(e) => e.stopPropagation()}>
      <Modal
        ref={favoriteModalRef}
        title={t(isModify ? '내 필터 이름 수정' : '내 필터 추가')}
        size={'x-small'}
        handleClose={isLoading ? undefined : closeModal}
        isBlockBackDropClose={isLoading}
        isCloseButtonDisabled={isLoading}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <FormGroup gapsize={'s'}>
              <IntranetLibraryLabel
                isRequired={true}
                title={t('내 필터 이름')}
                size={14}
              />
              <RHFTextArea
                required={true}
                name={'subject'}
                maxLength={80}
                minRows={6}
                placeholder={t(
                  '내 필터 이름을 등록해 주세요.\n등록한 이름은 언제든 수정할 수 있어요.',
                )}
              />
            </FormGroup>
            <SubmitContainer>
              <StyledCancelButton
                type="button"
                onClick={closeModal}
                disabled={isLoading}
              >
                {t('취소')}
              </StyledCancelButton>
              <StyledSubmitButton disabled={!isValid || isLoading}>
                {t('저장')}
                {isLoading && <Spinner size="10px" theme="white" />}
              </StyledSubmitButton>
            </SubmitContainer>
          </form>
        </FormProvider>
      </Modal>
    </div>
  );

  return {
    openModal,
    FavoriteModal,
    closeModal,
  };
};
